import React,{useState,useEffect} from 'react';
import {useNavigate,useSearchParams} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useAuth} from '../contexts/AuthContext';
import {supabase} from '../lib/supabase';
import { FiCheck, FiLoader, FiAlertCircle, FiArrowRight, FiRefreshCw, FiMail } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { trackSignUpEmailVerified } from '../services/analytics';
import Logo from '../components/common/Logo';

// Apple's web OAuth lands name in user_metadata.name as a single string, but
// the rest of the app (Onboarding skip logic, Settings, SmartResultCard) reads
// user_metadata.first_name / last_name. Backfill the structured fields once,
// matching what the native iOS path does in appleAuthService.persistAppleName.
const backfillAppleName = async (user) => {
  const meta = user?.user_metadata || {};
  if (meta.first_name && meta.last_name) return;
  const fullName = (meta.name || meta.full_name || '').trim();
  if (!fullName) return;
  const [firstName, ...rest] = fullName.split(/\s+/);
  const lastName = rest.join(' ');
  if (!firstName || !lastName) return;
  await supabase.auth.updateUser({
    data: { first_name: firstName, last_name: lastName, full_name: fullName },
  });
};

function AuthCallback() {
  const navigate=useNavigate();
  const [searchParams]=useSearchParams();
  const {signUp}=useAuth();
  
  const [status,setStatus]=useState('processing'); // 'processing', 'success', 'error'
  const [error,setError]=useState('');
  const [userEmail,setUserEmail]=useState('');
  const [resendLoading,setResendLoading]=useState(false);
  const [redirectTimer,setRedirectTimer]=useState(3);

  useEffect(()=> {
    handleCallback();
  },[]);

  // Resolution order:
  //   1. Provider returned an error (?error= or #error=) → show it.
  //   2. A session is already established → supabase-js's detectSessionInUrl
  //      consumed the hash before we ran (the common Apple OAuth path on web,
  //      where the URL ends up as `/auth/callback#` with everything stripped).
  //      Just navigate home.
  //   3. ?code= present → PKCE OAuth callback. Exchange for session.
  //   4. Otherwise → legacy email confirmation token flow.
  const handleCallback = async () => {
    try {
      const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
      const hashParams = new URLSearchParams(hash);
      const oauthError = searchParams.get('error') || searchParams.get('error_code') || hashParams.get('error');
      if (oauthError) {
        const desc = searchParams.get('error_description') || searchParams.get('error_message')
          || hashParams.get('error_description') || hashParams.get('error_message') || oauthError;
        setError(decodeURIComponent(desc.replace(/\+/g, ' ')));
        setStatus('error');
        return;
      }

      const {data: sessionData} = await supabase.auth.getSession();
      if (sessionData?.session) {
        await backfillAppleName(sessionData.session.user);
        navigate('/', {replace: true});
        return;
      }

      const code = searchParams.get('code');
      const type = searchParams.get('type');
      if (code && !type) {
        const {error} = await supabase.auth.exchangeCodeForSession(window.location.href);
        if (error) throw error;
        navigate('/', {replace: true});
        return;
      }

      handleEmailConfirmation();
    } catch (err) {
      console.error('Auth callback error:', err);
      setError(err.message || 'Sign in failed');
      setStatus('error');
    }
  };

  // Auto-redirect timer for success state
  useEffect(()=> {
    if (status==='success' && redirectTimer > 0) {
      const timer=setTimeout(()=> {
        setRedirectTimer(redirectTimer - 1);
      },1000);
      return ()=> clearTimeout(timer);
    } else if (status==='success' && redirectTimer===0) {
      navigate('/login');
    }
  },[status,redirectTimer,navigate]);

  const handleEmailConfirmation=async ()=> {
    try {
      // Get the token and type from URL parameters
      const accessToken=searchParams.get('access_token');
      const refreshToken=searchParams.get('refresh_token');
      const tokenType=searchParams.get('token_type');
      const type=searchParams.get('type');


      // Check if this is an email confirmation
      if (type !=='signup' && type !=='email_confirmation' && type !=='email_change') {
        throw new Error('Invalid confirmation type');
      }

      if (!accessToken || !refreshToken) {
        throw new Error('Missing authentication tokens');
      }

      // Set the session with the tokens from the URL
      const {data,error}=await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      });

      if (error) {
        console.error('Error setting session:',error);
        throw error;
      }

      if (!data.user) {
        throw new Error('No user data returned');
      }

      // Store user email for display and potential resend
      setUserEmail(data.user.email || '');

      // Check if email is now confirmed
      if (data.user.email_confirmed_at) {
        // Track email verification success
        trackSignUpEmailVerified(data.user.id);

        setStatus('success');
      } else {
        throw new Error('Email confirmation was not completed');
      }

    } catch (err) {
      console.error('Email confirmation error:',err);
      setError(err.message || 'Failed to confirm email address');
      setStatus('error');
    }
  };

  const handleResendVerification=async ()=> {
    if (!userEmail) {
      setError('No email address available for resend');
      return;
    }

    setResendLoading(true);

    try {
      // Attempt to resend confirmation email
      const {error}=await signUp(userEmail,'tempPassword123',{
        first_name: 'User',
        last_name: 'Verification',
        resend: true
      });

      if (error && !error.message.includes('already registered')) {
        throw error;
      }

      // Show success message and redirect to email confirmation page
      setTimeout(()=> {
        navigate('/email-confirmation',{
          state: {
            email: userEmail,
            userData: {resend: true}
          }
        });
      },1000);

    } catch (err) {
      console.error('Resend verification error:',err);
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleManualRedirect=()=> {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-app flex items-center justify-center p-4">
      <motion.div
        initial={{opacity: 0,y: 20,scale: 0.95}}
        animate={{opacity: 1,y: 0,scale: 1}}
        transition={{duration: 0.6}}
        className="max-w-md w-full"
      >
        {/* Logo Section */}
        <div className="text-center mb-10">
          <motion.div
            initial={{scale: 0.8,opacity: 0}}
            animate={{scale: 1,opacity: 1}}
            transition={{delay: 0.2}}
            whileHover={{scale: 1.05}}
            className="w-20 h-20 bg-gradient-to-br from-accent bg-accent 600  rounded-lg flex items-center justify-center shadow-md mx-auto mb-6"
          >
            <Logo size="xl" variant="white" />
          </motion.div>
          <h1 className="text-2xl lg:text-3xl font-semibold text-text-primary mb-3">
            {status==='processing' && 'Confirming Email...'}
            {status==='success' && 'Email Confirmed!'}
            {status==='error' && 'Confirmation Failed'}
          </h1>
          <p className="text-text-secondary font-medium">
            {status==='processing' && 'Please wait while we verify your email address'}
            {status==='success' && 'Welcome to TeeSync! Your account is now active'}
            {status==='error' && 'We encountered an issue confirming your email'}
          </p>
        </div>

        {/* Main Content */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.3}}
          className="bg-card rounded-lg border border-border-subtle p-8 backdrop-blur-sm"
        >
          {/* Processing State */}
          {status==='processing' && (
            <div className="text-center">
              <motion.div
                animate={{rotate: 360}}
                transition={{duration: 2,repeat: Infinity,ease: "linear"}}
                className="w-16 h-16 bg-gradient-to-br from-accent bg-accent 100  rounded-lg flex items-center justify-center mx-auto mb-6"
              >
                <SafeIcon icon={FiLoader} className="w-8 h-8 text-accent" />
              </motion.div>
              <h3 className="text-lg font-semibold text-text-primary mb-3">
                Verifying your email...
              </h3>
              <p className="text-text-secondary text-sm">
                This should only take a moment
              </p>
            </div>
          )}

          {/* Success State */}
          {status==='success' && (
            <div className="text-center">
              <motion.div
                initial={{scale: 0.8,opacity: 0}}
                animate={{scale: 1,opacity: 1}}
                transition={{delay: 0.4,type: "spring",stiffness: 200}}
                className="w-20 h-20 bg-accent rounded-lg flex items-center justify-center mx-auto mb-6"
              >
                <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-600" />
              </motion.div>
              
              <h3 className="text-xl font-bold text-text-primary mb-4">
                Email Confirmed Successfully!
              </h3>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <p className="text-green-800 font-medium mb-2">
                  🎉 Welcome to TeeSync!
                </p>
                <p className="text-green-700 text-sm">
                  Your email address has been verified and your account is now active. 
                  You can start scheduling golf rounds with your friends!
                </p>
              </div>

              {userEmail && (
                <div className="bg-accent/10 border border-accent rounded-xl p-4 mb-6">
                  <p className="text-accent font-medium text-sm">
                    Confirmed email: {userEmail}
                  </p>
                </div>
              )}

              {/* Auto-redirect countdown */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-blue-800 text-sm font-medium">
                  Redirecting to sign in in {redirectTimer} second{redirectTimer !== 1 ? 's' : ''}...
                </p>
              </div>

              {/* Manual redirect button */}
              <motion.button
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                onClick={handleManualRedirect}
                className="w-full flex items-center justify-center px-6 py-4 bg-accent text-white rounded-md hover:bg-interactive-hover transition-all duration-200 font-semibold "
              >
                Continue to Sign In
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </motion.button>
            </div>
          )}

          {/* Error State */}
          {status==='error' && (
            <div className="text-center">
              <motion.div
                initial={{scale: 0.8,opacity: 0}}
                animate={{scale: 1,opacity: 1}}
                transition={{delay: 0.4,type: "spring",stiffness: 200}}
                className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-lg flex items-center justify-center mx-auto mb-6"
              >
                <SafeIcon icon={FiAlertCircle} className="w-10 h-10 text-red-600" />
              </motion.div>
              
              <h3 className="text-xl font-bold text-text-primary mb-4">
                Confirmation Failed
              </h3>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <p className="text-red-800 font-medium mb-2">
                  Unable to confirm your email
                </p>
                <p className="text-red-700 text-sm mb-4">
                  {error || 'The confirmation link may have expired or already been used.'}
                </p>
              </div>

              {/* Error help section */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-orange-900 mb-3">What you can do:</h4>
                <div className="space-y-2 text-sm text-orange-800 text-left">
                  <p>• Check if you're using the most recent confirmation email</p>
                  <p>• Make sure you clicked the full link in the email</p>
                  <p>• Try requesting a new verification email</p>
                  <p>• Contact support if the issue persists</p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-3">
                {userEmail && (
                  <motion.button
                    whileHover={{scale: 1.02}}
                    whileTap={{scale: 0.98}}
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="w-full flex items-center justify-center px-6 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-orange-400 transition-all duration-200 font-semibold"
                  >
                    <SafeIcon icon={resendLoading ? FiLoader : FiRefreshCw} className={`w-5 h-5 mr-2 ${resendLoading ? 'animate-spin' : ''}`} />
                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                  </motion.button>
                )}
                
                <motion.button
                  whileHover={{scale: 1.02}}
                  whileTap={{scale: 0.98}}
                  onClick={handleManualRedirect}
                  className="w-full flex items-center justify-center px-6 py-3 border border-border-subtle text-text-secondary rounded-md hover:bg-surface transition-all duration-200 font-semibold"
                >
                  <SafeIcon icon={FiMail} className="w-5 h-5 mr-2" />
                  Back to Sign In
                </motion.button>
              </div>
            </div>
          )}

          {/* Help section for success and error states */}
          {(status==='success' || status==='error') && (
            <div className="mt-8 p-6 bg-surface border border-border-subtle rounded-lg">
              <h4 className="font-semibold text-text-primary mb-3">
                {status==='success' ? 'Next steps:' : 'Need help?'}
              </h4>
              <div className="space-y-2 text-sm text-text-secondary">
                {status==='success' ? (
                  <>
                    <p>• Sign in to your TeeSync account</p>
                    <p>• Create or join golf groups</p>
                    <p>• Schedule rounds with your friends</p>
                    <p>• Set your availability preferences</p>
                  </>
                ) : (
                  <>
                    <p>• Try using the most recent email in your inbox</p>
                    <p>• Check your spam folder for verification emails</p>
                    <p>• Make sure the link wasn't truncated when clicked</p>
                    <p>• Contact support if you continue having issues</p>
                  </>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={{delay: 1}}
          className="mt-8 text-center"
        >
          <p className="text-text-muted text-sm">
            Need help?{' '}
            <a
              href="mailto:support@teesync.app"
              className="text-accent hover:text-accent font-medium"
            >
              Contact Support
            </a>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default AuthCallback;